"use client";

import { useEffect, useState } from "react";
import { useBillingState } from "@/hooks/useBillingState";
import { getClientPlan } from "@/lib/plan";

import { EARLY_BIRD_DEADLINE } from "@repo/exam-config";

/** Early-bird pricing deadline (single source of truth in @repo/exam-config). */
export { EARLY_BIRD_DEADLINE };

export interface EarlyBirdCountdown {
  expired: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Under 72h — switch the urgency styling from amber to red. */
  critical: boolean;
  /** False during SSR/first paint; render placeholders until true to avoid hydration mismatch. */
  ready: boolean;
}

/**
 * Whether early-bird surfaces should show: the offer is still live AND the
 * user hasn't already bought a plan (no point selling to subscribers). Premium
 * resolves false until the billing mirror loads, so signed-out visitors and
 * free users never see a flicker; a premium user sees the banner drop out once
 * their state arrives.
 */
export function useEarlyBirdVisible(): boolean {
  const { expired } = useEarlyBirdCountdown();
  const { billing } = useBillingState();
  return !expired && !getClientPlan(billing).premium;
}

export function useEarlyBirdCountdown(): EarlyBirdCountdown {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ms = now === null ? 0 : EARLY_BIRD_DEADLINE.getTime() - now;
  const clamped = Math.max(0, ms);
  return {
    ready: now !== null,
    expired: now !== null && ms <= 0,
    critical: now !== null && ms > 0 && ms < 72 * 60 * 60 * 1000,
    days: Math.floor(clamped / 86_400_000),
    hours: Math.floor((clamped % 86_400_000) / 3_600_000),
    minutes: Math.floor((clamped % 3_600_000) / 60_000),
    seconds: Math.floor((clamped % 60_000) / 1000),
  };
}
